import React, { useState, useEffect, useContext } from "react";
import { Container, Spinner, Tooltip } from "react-bootstrap";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import GridMaster from "../masters/GridMaster";
import PlaceBidModal from "./PlaceBidModal";
import { AuthContext } from "../../contexts/AuthContext";
import { menuActions } from "../../store/menu-slice";
import { useDispatch } from "react-redux";
import { searchActions } from "../../store/grid-slice";
import { formatDateInDDMMYYYY } from "../../functions/common-functions";
import { getMaintainBidByUser } from "../../services/maintain-bid-service";
import { getPresignedURL } from "../../functions/upload";
import ActionComp from "../layouts/ActionComp";
import ActionRenderer from "../masters/ActionRenderer";

const MaintainBid = () => {
  const context = useContext(AuthContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [resetFilter, setResetFilter] = useState(true);
  const [showBidModal, setShowBidModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [reload, setReload] = useState(false);

  useEffect(() => {
    dispatch(menuActions.setMenu("Maintain Bid"));
    dispatch(searchActions.setSearch(""));
  }, [dispatch]);

  useEffect(() => {
    fetchEnquiries();
  }, [reload]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const enquiries = await getMaintainBidByUser(context.userEmail);
      const sortedEnquiries = enquiries.sort((a, b) => new Date(a.DueDate) - new Date(b.DueDate));
      setData(sortedEnquiries);
      setResetFilter(!resetFilter);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
    }
    setLoading(false);
  };

  const handlePlaceBid = (row) => {
    setSelectedEnquiry(row);
    setShowBidModal(true);
  };

  const handleFileDownload = async (enquiryNumber, fileName) => {
    try {
      const filePath = `EnquiryTender/${enquiryNumber.replace(/\//g, '_')}/${fileName}`;
      const downloadURL = await getPresignedURL(filePath);
      if (downloadURL) {
        window.open(downloadURL, '_blank');
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const columnDefs = [
    {
      headerName: "Enquiry Number",
      field: "EnquiryNumber",
      minWidth: 170,
      tooltipField: "EnquiryNumber",
    },
    {
      headerName: "Item Code",
      field: "ItemCode",
      minWidth: 120,
      tooltipField: "ItemCode",
    },
    {
      headerName: "Item Description",
      field: "ItemDescription",
      minWidth: 150,
      tooltipField: "ItemDescription",
    },
    {
      headerName: "Item Detail",
      field: "ItemDetail",
      minWidth: 150,
      tooltipField: "ItemDetail",
    },
    {
      headerName: "Item Type",
      field: "ItemType",
      minWidth: 120,
      tooltipField: "ItemType",
    },
    {
      headerName: "Quantity",
      field: "Quantity",
      minWidth: 100,
      tooltipField: "Quantity",
    },
    {
      headerName: "UOM",
      field: "UOM",
      minWidth: 80,
      tooltipField: "UOM",
    },
    {
      headerName: "GST%",
      field: "GST",
      minWidth: 80,
      tooltipField: "GST",
    },
    {
      headerName: "Division",
      field: "Division",
      minWidth: 120,
      tooltipField: "Division",
    },
    {
      headerName: "Location",
      field: "Location",
      minWidth: 120,
      tooltipField: "Location",
    },
    {
      headerName: "Logistic Company",
      field: "LogisticCompany",
      minWidth: 140,
      tooltipField: "LogisticCompany",
    },
    {
      headerName: "Remarks",
      field: "Remarks",
      minWidth: 150,
      tooltipField: "Remarks",
    },
    {
      headerName: "Attachment 1",
      field: "AttachImage",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachImage ? (
          <a href="#" onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 2",
      field: "AttachDocument1",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachDocument1 ? (
          <a href="#" onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 3",
      field: "AttachDocument2",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachDocument2 ? (
          <a href="#" onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 4",
      field: "AttachDocument3",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachDocument3 ? (
          <a href="#" onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 5",
      field: "ImagesAttachment",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && (params.data.ShowImageToCustomer || params.data.EnquirySource === "Manual") ? (
          <a href="#" onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Due Date",
      field: "DueDate",
      minWidth: 120,
      tooltipField: "DueDate",
      valueFormatter: (params) => {
        const displayDate = params.data.RevisedDueDate || params.data.DueDate;
        return displayDate ? formatDateInDDMMYYYY(displayDate.toString()) : "";
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (cellValue == null) return 0;
          const dateCell = new Date(cellValue);
          dateCell.setHours(0, 0, 0, 0);
          if (dateCell < filterLocalDateAtMidnight) return -1;
          if (dateCell > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    {
      headerName: "Contract/Non-Contract",
      field: "EnquirySource",
      minWidth: 150,
      valueGetter: (params) => {
        return params.data.EnquirySource === "Contract" ? "Contract Item" : "Non-Contract Item";
      }
    },
    {
      headerName: "Current Bid (₹)",
      field: "CurrentBid",
      minWidth: 120,
      valueGetter: (params) => {
        return params.data.CurrentBid || "No bid placed";
      }
    },
    {
      headerName: "Current Bid Date",
      field: "CurrentBidDate",
      minWidth: 120,
      tooltipField: "CurrentBidDate"
    },
    {
      headerName: "Action",
      field: "",
      maxWidth: 120,
      cellRenderer: (params) => (
        <ActionRenderer
          selectedData={params.data}
          handlePlaceBidEvent={handlePlaceBid}
          actionName={{ ViewAction: true, EditAction: false, DeleteAction: false }}
        />
      ),
      sortable: false,
      filter: false,
      pinned: 'right',
      resizable: false
    }
  ];

  const headerName = (field) => {
    const mapping = {
      "EnquiryNumber": "Enquiry Number",
      "ItemCode": "Item Code",
      "ItemDescription": "Item Description",
      "ItemDetail": "Item Detail",
      "ItemType": "Item Type",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST": "GST%",
      "Location": "Location",
      "Division": "Division",
      "LogisticCompany": "Logistic Company",
      "Remarks": "Remarks",
      "AttachImage": "Attachment 1",
      "AttachDocument1": "Attachment 2",
      "AttachDocument2": "Attachment 3",
      "AttachDocument3": "Attachment 4",
      "ImagesAttachment": "Attachment 5",
      "DueDate": "Due Date",
      "EnquirySource": "Contract/Non-Contract",
      "CurrentBid": "Current Bid (₹)",
      "CurrentBidDate": "Current Bid Date"
    };
    return mapping[field] || "No match found";
  };

  const fieldName = (header) => {
    const mapping = {
      "Enquiry Number": "EnquiryNumber",
      "Item Code": "ItemCode",
      "Item Description": "ItemDescription",
      "Item Detail": "ItemDetail",
      "Item Type": "ItemType",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST%": "GST",
      "Location": "Location",
      "Division": "Division",
      "Logistic Company": "LogisticCompany",
      "Remarks": "Remarks",
      "Attachment 1": "AttachImage",
      "Attachment 2": "AttachDocument1",
      "Attachment 3": "AttachDocument2",
      "Attachment 4": "AttachDocument3",
      "Attachment 5": "ImagesAttachment",
      "Due Date": "DueDate",
      "Contract/Non-Contract": "EnquirySource",
      "Current Bid (₹)": "CurrentBid",
      "Current Bid Date": "CurrentBidDate"
    };
    return mapping[header] || "No match found";
  };

  return (
    <>
      {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <ActionComp
          label={"Maintain Bid"}
          instructionText="To place bid kindly click on icon placed on Action column of below dashboard."
        />
        <GridMaster
          rowDetails={data}
          colDetails={columnDefs}
          fieldNames={fieldName}
          headerNames={headerName}
          getDataEvent={() => { }}
          getFilterColLength={() => { }}
          setExcelData={() => { }}
          resetind={resetFilter}
          handlePlaceBidEvent={handlePlaceBid}
        />
      </Container>

      {showBidModal && (
        <PlaceBidModal
          show={showBidModal}
          onHide={() => setShowBidModal(false)}
          enquiry={selectedEnquiry}
          setReload={setReload}
          reload={reload}
        />
      )}
    </>
  );
};

export default MaintainBid;
